import type { AnimateSlideType } from '~/hooks/interaction/form/useSlideAnimation';
import type { AnswerType } from '~/model/AnswerType';
import type { AnswerUserType } from '~/model/AnswerUserType';
import type { ConclusionType } from '~/model/ConclusionType';
import type { LocaleType, UriType } from '~/model/GlobalTypes';
import type { QuestionReconstructedType } from '~/model/QuestionType';
import type { SectionType } from '~/model/SectionType';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from '@/app/[locale]/(form)/components/FormProvider';
import { useContributorContext } from '@/app/[locale]/(form)/components/provider/ContributorProvider';
import { fetchReconstructedQuestion } from '@/app/actions/server/questions';
import { useApiErrorContext, useProgressContext } from '@/app/components/Provider';
import { isEntityEmpty, reorganizeSections } from '@/app/utils/entities/interactions';
import { getIdFromUri } from '@/app/utils/entities/uri';
import { buildDefaultConclusion } from '~/components/Interaction/utils';
import { apiTypes } from '~/const/api';
import { ElementType } from '~/const/appConst';
import { localeSubpaths } from '~/const/locale';
import { isGuestUser } from '~/utils/user/user';
import { formatAnswerValue, getFormattedAnswerValue } from '~/utils/utils';

type UserJourneyType = QuestionReconstructedType | SectionType | ConclusionType;

export function useUserJourney(
  languageToDisplay: LocaleType,
  previewForm: boolean,
) {
  const t = useTranslations();

  const { setError } = useApiErrorContext();
  const { answers, contributor, setAnswers } = useContributorContext();
  const { setIndex, total } = useProgressContext();
  const { conclusions, interaction, questions, sections, setQuestions } = useFormContext();

  const email: Partial<UserJourneyType> = { type: apiTypes.EMAIL };

  const [userJourney, setUserJourney] = useState<UserJourneyType[]>(() => {
    const interactionConclusion = conclusions.find(conclusion => !conclusion?.branch_choice) || buildDefaultConclusion(
      t(`${interaction['@type'].toUpperCase() as 'SURVEY' | 'SELECTION'}.DEFAULT_CONCLUSION`),
    );

    return [
      ...reorganizeSections(sections, questions),
      isGuestUser(contributor) && interaction?.result_visible_on_front && email,
      interactionConclusion,
    ].filter(step => step);
  });

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

  const [answerUser, setAnswerUser] = useState<AnswerUserType | AnswerType>({});
  const [isLoading, setIsLoading] = useState(false);

  const { SECTION, QUESTION, CONCLUSION } = ElementType;

  const currentStep = userJourney[currentStepIndex];
  const currentStepType = currentStep?.['@type'];
  const currentStepUri = currentStep?.['@id'];

  const hasNextConclusion = conclusions.some(
    conclusion =>
      conclusion['@id']
      === (currentStep as QuestionReconstructedType)?.next_step,
  );

  const getConclusionOnConditional = (question: QuestionReconstructedType) => {
    if (question.type !== apiTypes.CONDITIONAL)
      return null;

    return conclusions.find(
      conclusion =>
        question.next_step?.[
          answerUser?.[question?.['@id']]?.answer_choices?.[0]
        ] === conclusion['@id'],
    );
  };

  const hasNextStep = (step: QuestionReconstructedType) => {
    if (!step?.next_step)
      return false;

    return step.next_step === 'string'
      ? true
      : Object.values(step.next_step).some(value => value);
  };

  const isLastStep
    = hasNextConclusion
      || (!hasNextStep(currentStep as QuestionReconstructedType)
        && userJourney[currentStepIndex + 1]?.['@type'] === CONCLUSION)
      || currentStepIndex === userJourney.length - 1;

  const initAnswer = (
    answer: AnswerType,
    question: QuestionReconstructedType,
  ) => {
    setAnswerUser(
      answer
        ? ({
            [question['@id']]: !previewForm
              ? {
                  ...formatAnswerValue(answers, answer, question),
                  type: question.type,
                }
              : getFormattedAnswerValue(
                  answer[currentStepUri],
                  currentStep?.['@type'],
                ),
          } as AnswerUserType)
        : {},
    );
  };

  const getNextQuestion = async (uri: UriType) => {
    return await fetchReconstructedQuestion({
      id: getIdFromUri(uri),
      locale: localeSubpaths[languageToDisplay],
    });
  };

  const handleNextStep = async (uri: UriType) => {
    setIsLoading(true);

    const previouslyFetchedQuestion = questions.find(
      question => question['@id'] === uri,
    );

    const conclusion = conclusions.find(
      conclusion => conclusion['@id'] === uri,
    );

    if (!conclusion) {
      const question
        = previouslyFetchedQuestion || (await getNextQuestion(uri));
      setUserJourney([
        ...userJourney.slice(0, currentStepIndex + 1),
        question,
        ...userJourney
          .slice(currentStepIndex + 1)
          .filter(step => step['@type'] !== QUESTION),
      ]);
      setQuestions(
        previouslyFetchedQuestion ? questions : [...questions, question],
      );
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setUserJourney(
        [
          ...userJourney.slice(0, currentStepIndex + 1),
          isGuestUser(contributor) && interaction?.result_visible_on_front && email,
          conclusion,
        ].filter(step => step),
      );
    }
  };

  const handlePreviewFormAnswers = (
    answer: AnswerType,
    stepUri: UriType,
    stepType: string,
  ) => {
    if (previewForm && stepType === QUESTION && answer) {
      setAnswers(
        answers.some(
          previousAnswer => previousAnswer.question === stepUri,
        )
          ? answers.map((previousAnswer) => {
              return previousAnswer[stepUri]
                ? ({
                    ...answerUser,
                    question: stepUri,
                  } as AnswerType)
                : previousAnswer;
            })
          : [
              ...answers,
              { ...answerUser, question: stepUri } as AnswerType,
            ],
      );
    }
  };

  const handleNextPrev = async (
    newIndex: number,
    onSubmit: (newIndex: number) => Promise<void>,
    animation: AnimateSlideType,
    shouldRetry = true,
  ) => {
    const step = currentStep as QuestionReconstructedType;
    const nextStepUri = step?.next_step;
    const isNext = newIndex > currentStepIndex;
    const currentStepUri = step?.['@id'];
    const currentStepType = step?.['@type'];
    const currentAnswer = answerUser?.[currentStepUri];

    try {
      if (isNext && step.type === apiTypes.CONDITIONAL) {
        const nextStep = nextStepUri?.[currentAnswer?.answer_choices[0]];
        if (nextStep && !userJourney.some(step => step['@id'] === nextStep))
          await handleNextStep(nextStep);
      } else if (isNext && typeof nextStepUri === 'string') {
        await handleNextStep(nextStepUri);
      }

      if (!previewForm && (currentAnswer || (isLastStep && isNext)))
        await onSubmit(newIndex);

      handlePreviewFormAnswers(currentAnswer, currentStepUri, currentStepType);

      await animation({
        callback: async () => Promise.resolve(setCurrentStepIndex(newIndex)),
        back: !isNext,
      }).then(() => {
        if (!isNext && userJourney[newIndex]?.['@type'] === QUESTION) {
          setUserJourney([
            ...userJourney.slice(0, newIndex + 1),
            ...userJourney
              .slice(newIndex + 1)
              .filter(step => step?.['@type'] !== QUESTION),
          ]);
        }
      });
    } catch (error) {
      if (shouldRetry && (error as { response: Response }).response) {
        handleNextPrev(newIndex, onSubmit, animation, false);
      } else {
        setError(error);
        throw error;
      }
    }
  };

  const handleOnCurrentStepIsSection = async (section: SectionType) => {
    const previouslyFetchedQuestion = questions.find(
      question => question['@id'] === section.questions[0],
    );

    const question = previouslyFetchedQuestion || (await getNextQuestion(section.questions[0]));
    const isFirstSectionEmpty = isEntityEmpty(section) && currentStepIndex === 0;

    setQuestions(
      previouslyFetchedQuestion ? questions : [...questions, question],
    );
    setUserJourney([
      ...userJourney.slice(isFirstSectionEmpty ? 1 : 0, currentStepIndex + 1),
      question,
      ...userJourney
        .slice(currentStepIndex + 1)
        .filter(step => step?.['@type'] !== QUESTION),
    ]);
  };

  const onCurrentStepChange = useCallback(async () => {
    const previousAnswer = currentStepType === QUESTION
      ? answers?.find(answer => answer.question === currentStepUri)
      : null;

    switch (currentStepType) {
      case SECTION:
        setIsLoading(true);

        if ((currentStep as SectionType)?.questions.length) {
          await handleOnCurrentStepIsSection(currentStep);
        }

        setIsLoading(false);
        break;
      case QUESTION:
        setIndex(() => {
          const questionsUri = sections.map(section => section.questions).flat();
          const questionIndex = questionsUri.findIndex(uri => currentStep['@id'] === uri);

          return total - questionsUri.length + questionIndex;
        });

        initAnswer(previousAnswer, currentStep as QuestionReconstructedType);
        break;
      case CONCLUSION:
        setIndex(total);
        break;
      default:
        setIndex(total - 1);
        break;
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, currentStep?.['@id']]);

  useEffect(() => {
    onCurrentStepChange();
  }, [onCurrentStepChange]);

  return {
    answerUser: answerUser as AnswerUserType,
    currentStep,
    currentStepIndex,
    userJourney,
    sections,
    questions,
    isLastStep,
    isLoading,
    handleNextPrev,
    setIsLoading,
    setCurrentStepIndex,
    setAnswerUser,
    getConclusionOnConditional,
  };
}
